import { Formik, getIn } from 'formik';
import { get } from 'lodash';
import React, { useEffect } from 'react';
import Input from 'react-phone-number-input/input';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { object, string } from 'yup';
import userImage from '../../../assets/img/placeHolderImage.png';
import config from '../../../config';
import convertJsonToFormData from '../../helpers/helper';
import { showModal } from '../../redux/error/ErrorAction';
import { hideLoading, showLoading } from '../../redux/loader/LoaderActions';
import { POST } from '../../services/rest.service';
import TextField from '../UI/TextField';
import { getLoggedInUser } from '../../redux/user/UserActions';
import { getClientName, localNumber } from '../../helpers/utils';

const UpdateProfile = (props) => {
  const dispatch = useDispatch();

  // Re-read the signed-in user when the page opens. The subscription state can
  // change from a phone at any moment (a STOP reply), and the store otherwise
  // holds whatever was true at sign-in.
  useEffect(() => {
    dispatch(getLoggedInUser(props.history));
  }, [dispatch, props.history]);
  let history = useHistory();
  const user = useSelector((state) => state.user.user);
  const serviceData = useSelector((state) => state.enumData);
  const cType = user.sub_type || user.type;

  // This screen is shared by six roles. Bank details concern only the two that
  // get paid, and `type` is used rather than `cType` because sub_type carries
  // the corporate variants for importers, not agents.
  const isAgent = user.type === 'clearing_agent' || user.type === 'forwarding_agent';
  const hasBankOnFile = !!get(user, 'bank_account_number', '');

  return (
    <div id="tabblock1" className="tabcontent">
      <section className="bodycont">
        <div>
          {/* <h2>My Profile</h2> */}

          <Formik
            initialValues={{
              clientSelected: props.type === 'client' ? true : false,
              first_name: get(user, 'first_name', '') || '',
              last_name: get(user, 'last_name', '') || '',
              company_name: get(user, 'company_name', '') || '',
              company_address: get(user, 'company_address', '') || '',
              avatar: get(user, 'avatar', ''),
              // Prefilled from me(), which surfaces these for the owner only.
              bank_uuid: get(user, 'bank_uuid', '') || '',
              bank_account_name: get(user, 'bank_account_name', '') || '',
              bank_account_number: get(user, 'bank_account_number', '') || '',
              // Read-only on this form: everyone is subscribed by default and
              // switches off by replying STOP on WhatsApp, not here. Held only
              // so the status line can say which state they are in.
              whatsapp_opt_in: !!get(user, 'whatsapp_opt_in', true),
              // Shows the number actually messaged: the separate WhatsApp
              // number if one is set, otherwise the mobile number.
              whatsapp_number: localNumber(
                get(user, 'whatsapp_number', '') || get(user, 'mobile_number', ''),
              ),
            }}
            onSubmit={(values, { setSubmitting }) => {
              const payload = {
                mobile_number: get(user, 'mobile_number', ''),
                email: get(user, 'email', ''),
                user_uuid: get(user, 'uuid', ''),
              };

              if (cType === 'application_client') {
                payload.first_name = values.first_name;
                payload.last_name = values.last_name;
              }
              if (cType !== 'application_client') {
                payload.company_name = values.company_name;
                payload.company_address = values.company_address;
              }

              // Sent only for agents, and only when something was actually
              // entered. The API resets an approved agent to pending whenever
              // these change, so posting them unchanged on every profile save
              // would be a slow way of suspending everyone's bidding.
              if (isAgent && values.bank_uuid) {
                payload.bank_uuid = values.bank_uuid;
                payload.bank_account_name = values.bank_account_name;
                payload.bank_account_number = values.bank_account_number;
              }

              // Only the number is editable here. Subscription is not sent:
              // it is switched off by replying STOP on WhatsApp and back on
              // with START, and the server keeps that state.
              //
              // The field shows the mobile number when no separate WhatsApp
              // number is set. Saving that unchanged must not store a copy of
              // it: the mobile number is already the fallback, and a copy
              // would go stale if the mobile number were ever corrected. So a
              // separate number is kept only when it differs, compared in the
              // local form so "+234803..." and "0803..." count as the same.
              const entered = String(values.whatsapp_number || '').trim();
              payload.whatsapp_number =
                entered && localNumber(entered) !== localNumber(get(user, 'mobile_number', ''))
                  ? entered
                  : '';

              dispatch(showLoading());
              POST(`/users/update`, payload)
                .then(({ data }) => {
                  dispatch(showModal('Profile updated successfully!', true));
                  dispatch({
                    type: 'USER',
                    payload: {
                      user: data.user,
                      type: data.user.type,
                    },
                  });
                  dispatch(getLoggedInUser(props.history));
                  dispatch(hideLoading());
                  history.goBack();
                })
                .catch((err) => {
                  dispatch(showModal(err.message));
                  dispatch(hideLoading());
                  setSubmitting(false);
                });
            }}
            validationSchema={object().shape({
              // Bank details are optional on this form — an agent may be here
              // to change their company address and nothing else. But a bank
              // chosen without an account number, or a malformed number, is
              // rejected rather than half-saved.
              bank_account_number: string().when('bank_uuid', {
                is: (v) => !!v,
                then: string()
                  .required('Account number is required')
                  .matches(/^[0-9]{10}$/, 'Account number must be exactly 10 digits'),
              }),
              bank_account_name: string().when('bank_uuid', {
                is: (v) => !!v,
                then: string()
                  .trim()
                  .required('Account name is required'),
              }),
              first_name: string().when('clientSelected', {
                is: true,
                then: string().required('First name is required'),
              }),
              last_name: string().when('clientSelected', {
                is: true,
                then: string().required('Last name is required'),
              }),
              company_name: string().when('clientSelected', {
                is: false,
                then: string().required('Company name is required'),
              }),
              company_address: string().when('clientSelected', {
                is: false,
                then: string().required('Address is required'),
              }),
              // Optional, because leaving it blank means "use the number
              // already on my account". Otherwise 0 followed by ten digits,
              // matching the API rule. The mobile number shown unchanged is
              // not checked: it is not saved as a copy, and an old number in
              // another shape must not block saving the rest of the profile.
              whatsapp_number: string()
                .trim()
                .test(
                  'local-whatsapp-number',
                  'Enter an 11-digit number starting with 0',
                  (value) =>
                    !value ||
                    localNumber(value) === localNumber(get(user, 'mobile_number', '')) ||
                    /^0[0-9]{10}$/.test(value),
                ),
            })}
          >
            {(renderProps) => {
              const {
                values,
                touched,
                errors,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
                setFieldValue,
              } = renderProps;

              return (
                <form onSubmit={handleSubmit}>
                  <div className="personal-info">
                    <div className="profilesec">
                      <div className="profileimage">
                        <img
                          src={
                            get(user, 'avatar')
                              ? `${config.api.URL.replace('/api/v1', '')}/${values.avatar}`
                              : userImage
                          }
                          alt=""
                          height="170px"
                          weight="170px"
                        />
                        <span>
                          <input
                            className="d-none"
                            id={'profile'}
                            type="file"
                            accept="image/jpeg,image/jpg,image/png,image/svg"
                            name="profile"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                dispatch(showLoading());
                                const payload = {
                                  avatar: file,
                                };
                                let formData = convertJsonToFormData(payload);
                                POST('/users/uploadAvatar', formData)
                                  .then(({ data }) => {
                                    setFieldValue('avatar', data.avatar);
                                    dispatch(getLoggedInUser(props.history));

                                    dispatch(hideLoading());
                                  })
                                  .catch((err) => {
                                    dispatch(showModal(err.message));
                                    dispatch(hideLoading());
                                  });
                              }
                            }}
                          />
                          <label htmlFor="profile">
                            <span className="profile-camera">
                              <i className="fa fa-camera" aria-hidden="true"></i>
                            </span>
                          </label>
                        </span>
                      </div>
                      {getClientName(user)}
                    </div>

                    <h3>Account Information</h3>

                    <div className="row">
                      <div className="col-sm-6 text-left mb-2">
                        <label>Email:</label>
                        <TextField
                          id="email"
                          type="email"
                          className="form-control "
                          value={get(user, 'email', '')}
                          disabled={true}
                        />
                      </div>
                      <div className="col-sm-6 text-left mb-2">
                        <label>Mobile:</label>
                        <Input
                          value={get(user, 'mobile_number', '')}
                          id="mobile_number"
                          className={`form-control`}
                          disabled={true}
                          onChange={() => {}}
                        />
                      </div>

                      {cType === 'application_client' ? (
                        <>
                          <div className="col-sm-6 text-left mb-2">
                            <label>First Name:</label>
                            <TextField
                              id="first_name"
                              type="text"
                              className="form-control "
                              placeholder="First Name*"
                              value={values.first_name}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={getIn(touched, 'first_name') && getIn(errors, 'first_name')}
                            />
                          </div>
                          <div className="col-sm-6 text-left mb-2">
                            <label>Last Name:</label>
                            <TextField
                              id="last_name"
                              type="text"
                              className="form-control "
                              placeholder="Last Name*"
                              value={values.last_name}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={getIn(touched, 'last_name') && getIn(errors, 'last_name')}
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="col-sm-6 text-left mb-2">
                            <label>Company Name:</label>
                            <TextField
                              id="company_name"
                              type="text"
                              className="form-control "
                              placeholder="Company Name*"
                              value={values.company_name}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={
                                getIn(touched, 'company_name') && getIn(errors, 'company_name')
                              }
                            />
                          </div>

                          <div className="col-sm-6 text-left mb-2">
                            <label>Company Address:</label>
                            <TextField
                              id="company_address"
                              type="text"
                              className="form-control "
                              placeholder={'Company Reg. Address*'}
                              value={values.company_address}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={
                                getIn(touched, 'company_address') &&
                                getIn(errors, 'company_address')
                              }
                            />
                          </div>
                        </>
                      )}

                      {/* Only the two roles GoAgent pays.
                          The warning is here because the consequence is
                          otherwise invisible: changing these sends an approved
                          agent back to the compliance queue and stops them
                          bidding until a compliance officer re-approves. Adding
                          them for the first time is free, which is what lets
                          existing agents comply without losing a day's work. */}
                      {isAgent && (
                        <>
                          <div className="col-sm-12 text-left mt-3 mb-2">
                            <h5>Bank Account for Payments</h5>
                            {hasBankOnFile ? (
                              <div className="alert alert-warning py-2">
                                Changing these details will suspend your bidding until a compliance
                                officer re-approves your account.
                              </div>
                            ) : (
                              <p className="text-muted">
                                You need a bank account on file before you can place a bid. Adding
                                it now will not affect your approval status.
                              </p>
                            )}
                          </div>

                          <div className="col-sm-6 text-left mb-2">
                            <label>Bank:</label>
                            <select
                              id="bank_uuid"
                              name="bank_uuid"
                              className="form-control"
                              value={values.bank_uuid}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            >
                              <option value="">Select Bank</option>
                              {serviceData?.banks?.length > 0 &&
                                serviceData.banks.map((b) => (
                                  <option value={b.uuid} key={b.uuid}>
                                    {b.bank_name}
                                  </option>
                                ))}
                            </select>
                          </div>

                          <div className="col-sm-6 text-left mb-2">
                            <label>Account Name:</label>
                            <TextField
                              id="bank_account_name"
                              type="text"
                              className="form-control"
                              placeholder="Account Name"
                              value={values.bank_account_name}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={
                                getIn(touched, 'bank_account_name') &&
                                getIn(errors, 'bank_account_name')
                              }
                            />
                          </div>

                          <div className="col-sm-6 text-left mb-2">
                            <label>Account Number:</label>
                            <TextField
                              id="bank_account_number"
                              type="text"
                              className="form-control"
                              placeholder="10 digits"
                              value={values.bank_account_number}
                              maxLength={10}
                              inputMode="numeric"
                              onChange={(e) => {
                                e.target.value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                                handleChange(e);
                              }}
                              onBlur={handleBlur}
                              error={
                                getIn(touched, 'bank_account_number') &&
                                getIn(errors, 'bank_account_number')
                              }
                            />
                          </div>
                        </>
                      )}

                      {/* WhatsApp.
                          Everyone is subscribed by default: users were told
                          before registering and agreed. There is no switch
                          here on purpose — turning it off is a STOP reply on
                          WhatsApp and turning it back on is START, both
                          handled by the webhook. What a person can change here
                          is the number. */}
                      <div className="col-sm-12 text-left mb-2 mt-3">
                        <hr />
                        <label>WhatsApp Updates:</label>
                        <p className="mb-1" id="whatsapp_status">
                          {get(user, 'whatsapp_opt_in', true) !== false ? (
                            <>
                              Job updates are sent to you on WhatsApp. Reply <strong>STOP</strong> on
                              WhatsApp to turn them off.
                            </>
                          ) : (
                            <>
                              WhatsApp updates are off. Reply <strong>START</strong> on WhatsApp to
                              turn them back on.
                            </>
                          )}
                        </p>
                      </div>

                      <div className="col-sm-6 text-left mb-2">
                        <label htmlFor="whatsapp_number">Current WhatsApp Number:</label>
                        <TextField
                          id="whatsapp_number"
                          type="text"
                          className="form-control"
                          value={values.whatsapp_number}
                          maxLength={11}
                          inputMode="numeric"
                          // Digits only, at most eleven: anything else is
                          // dropped as it is typed or pasted.
                          onChange={(e) =>
                            setFieldValue(
                              'whatsapp_number',
                              e.target.value.replace(/\D/g, '').slice(0, 11),
                            )
                          }
                          onBlur={handleBlur}
                          error={
                            getIn(touched, 'whatsapp_number') && getIn(errors, 'whatsapp_number')
                          }
                        />
                      </div>
                    </div>
                    <div className="buttonsec">
                      <button
                        type="button"
                        onClick={() => history.goBack()}
                        className="submitbut btn-light"
                      >
                        Back
                      </button>
                      <button className="submitbut" type="submit" disabled={isSubmitting}>
                        Save
                      </button>
                    </div>
                  </div>
                </form>
              );
            }}
          </Formik>
        </div>
      </section>
    </div>
  );
};

export default UpdateProfile;
